import { EnvironmentService } from '../services/environment.service';
import { ApiConfig } from '../services/api-config';
import { LogLevelTypes } from '../types/log.types';
import { AUTH_BEARER_STORAGE_KEY } from '../utils/stored-json';

export class URLType {
    public static LOCAL_RELATIVE = 'local-relative';
    public static LOCAL_ABSOLUTE = 'local-absolute';
    public static REMOTE = 'remote';
}

export type URLTypes = 'local-relative' |
    'local-absolute' |
    'remote';

export type PackageType = 'PACKAGE_TYPE_BASIC' | 'PACKAGE_TYPE_PRO';
export class Packages {
    static PACKAGE_TYPE_BASIC: PackageType = 'PACKAGE_TYPE_BASIC';
    static PACKAGE_TYPE_PRO: PackageType = 'PACKAGE_TYPE_PRO';
}

export class LoginServiceConstants {
    public static AUTH_BEARER_STORAGE_KEY = AUTH_BEARER_STORAGE_KEY;
}

export class SettingsServiceConstants {
    public static MOTIVE_STORAGE_KEY = 'MOTIVE';
}

export class Commons {
    static DEBUG = EnvironmentService.getBoolEnv('NEXT_PUBLIC_DEBUG');
    // Getter (not a static field) so tests can spy on it via
    // jest.spyOn(Commons, 'IS_PROD', 'get').mockReturnValue(true).
    static get IS_PROD() {
        return EnvironmentService.env.NEXT_PUBLIC_ENVIRONMENT == "prod";
    }
    static get DEBUG_LEVEL() {
        return Commons.IS_PROD ? LogLevelTypes.INFO : LogLevelTypes.TRACE_7
    };

    /**
     * Payment gateway test vs live mode is decided at access time based on
     * the current hostname via ApiConfig helpers. Localhost and QA domains
     * use test credentials; every other host (prod) uses live credentials.
     * Using getters (not static constants) so the hostname is read when the
     * browser runtime asks, not at module-load time during SSR/SSG.
     */
    static get PAYMENT_GATEWAY_TEST_MODE(): boolean {
        return ApiConfig.isLocalhost() || ApiConfig.isQA();
    }
    static get PAYPAL_TEST_MODE(): boolean {
        return Commons.PAYMENT_GATEWAY_TEST_MODE;
    }

    static get PAYPAL_CLIENT_ID(): string {
        return Commons.PAYPAL_TEST_MODE
            ? 'AdZHSnXdmvLFV3rl5BlvsV-C69wKsI2tKFymJUy8FElT7nwhB86mN9fWfW3BuKtlyQIkcNEnTG1uxMO6'
            : 'AXxQSmbb22oe4ZjbHHGlTGZPP1iTePApytBxhHBeooClTimic2uX8gQwkWnVJRjKcoQ5Wyksk9Nz2pkl';
    }

    static get PAYPAL_PLAN_BACKER_MONTHLY(): string {
        return Commons.PAYPAL_TEST_MODE ? 'P-2LS877027G7055319L3OWXGI' : 'P-8W460836KX511983SL3SFLRY';
    }
    static get PAYPAL_PLAN_BACKER_YEARLY(): string {
        return Commons.PAYPAL_TEST_MODE ? 'P-0C150984PD4532505L3OWWVQ' : 'P-4BE32205WC8099423L3SFM5Y';
    }
    static get PAYPAL_PLAN_PATRON_MONTHLY(): string {
        return Commons.PAYPAL_TEST_MODE ? 'P-21K9973416833342TL3OWXOA' : 'P-7R9961430T230120CL3SFMDA';
    }
    static get PAYPAL_PLAN_PATRON_YEARLY(): string {
        return Commons.PAYPAL_TEST_MODE ? 'P-4SW19357A6322715YL3OWXVA' : 'P-1H169679XR034493GL3SFMMI';
    }

    static STRIPE_LIVE_PUBLIC_KEY = 'pk_live_CK9WSyLfkPFmZehZljDUlyUh';
    static STRIPE_TEST_PUBLIC_KEY = 'pk_test_JX5QjY8Dpl8OhznCmkro01S8';
    static get STRIPE_PUBLIC_KEY(): string {
        return Commons.PAYMENT_GATEWAY_TEST_MODE ? Commons.STRIPE_TEST_PUBLIC_KEY : Commons.STRIPE_LIVE_PUBLIC_KEY;
    }

    static REMOVE_STORED_FILE_AFTER_HOURS = 6;

    static DOMAINS = {
        COMPRESSPDF_DEV: {
            key: 'COMPRESSPDF_DEV',
            url: 'https://www.compresspdf.dev',
            title: 'PDF Compressor',
            description: 'Compress PDF document files online for free',
            type: URLType.REMOTE,
        },
        JPGTOPDF_DEV: {
            key: 'JPGTOPDF_DEV',
            url: 'https://www.jpgtopdf.dev',
            title: 'JPG To PDF',
            description: 'Convert JPG to PDF online for free',
            type: URLType.REMOTE,
        },
        MERGEPDF_DEV: {
            key: 'MERGEPDF_DEV',
            url: 'https://www.mergepdf.dev',
            title: 'Merge PDF',
            description: 'Online Merge PDF document for free',
            type: URLType.REMOTE,
        },
        PDFTOJPG_DEV: {
            key: 'PDFTOJPG_DEV',
            url: 'https://www.pdftojpg.dev',
            title: 'PDF To JPG',
            description: 'Free convert PDF to JPEG/JPG online',
            type: URLType.REMOTE,
        },
        PDFTOWORD_DEV: {
            key: 'PDFTOWORD_DEV',
            url: 'https://www.pdftoword.dev',
            title: 'PDF To Word',
            description: 'Free online PDF to Word (DOCX/DOC) converter',
            type: URLType.REMOTE,
        },
        VIDEOCOMPRESSOR_DEV: {
            key: 'VIDEOCOMPRESSOR_DEV',
            url: 'https://www.videocompressor.dev',
            title: 'Compress Video',
            description: 'Free online WEBM and MP4 video compressor',
            type: URLType.REMOTE,
        },
        WORDTOPDF_DEV: {
            key: 'WORDTOPDF_DEV',
            url: 'https://www.wordtopdf.dev',
            title: 'Word To PDF',
            description: 'Convert Word/DOCX/DOC files to PDF documents for free',
            type: URLType.REMOTE,
        },
        XCONVERT_COM: {
            key: 'XCONVERT_COM',
            url: 'https://www.xconvert.com',
            title: 'File Tools',
            description: 'Online File Convert/Merge/Split and Compress Tool',
            type: URLType.REMOTE,
        },
    };

};

